import React from 'react'
import { Box, Chip, Link, Stack, Tooltip, Typography } from '@mui/material'
import { withTooltip } from './muiElements'
import { FolderOutlined } from '@mui/icons-material'
import { handleLightspeedLinkClick } from '../../../utils/externalLinks'
import { formatMainCategory } from '../../../utils/productCategory'
import {
  classifyProductType,
  ProductType,
  PRODUCT_TYPE_META,
  PRODUCT_TYPE_SORT_ORDER,
} from '../../../utils/productType'
import ProductTypeChip from '../../../components/ProductTypeChip'
import IssuesChip from './IssuesChip'
import type { RenderContext } from '../types'

export const PRODUCT_NAME_MIN_WIDTH = 200

// Tuned for the narrow identity column on inspector tables.
const SKU_MAX_DISPLAY_LENGTH = 20

export const truncateSkuMiddle = (sku: string): string => {
  if (sku.length <= SKU_MAX_DISPLAY_LENGTH) return sku
  return `${sku.slice(0, 11)}...${sku.slice(-12)}`
}

const MISMATCH_TEXT_FG = '#D92D52'
const MISMATCH_BG = '#FFF1F3'
const MISMATCH_PILL_SX: Record<string, any> = {
  color: MISMATCH_TEXT_FG,
  backgroundColor: MISMATCH_BG,
  borderRadius: '4px',
  paddingLeft: '4px',
  paddingRight: '4px',
}

const PRODUCT_NAME_HOVER_CLASS = 'pwx-product-name-hover'

export interface ProductNameOptions {
  linkColor?: string
  linkFontSize?: string | number
  nameMismatch?: boolean
}

export interface ProductNameWithMetaOptions extends ProductNameOptions {
  showCategory?: boolean
  showTypeBadge?: boolean
  showInactiveChip?: boolean
  skuMismatch?: boolean
}

// inline-block keeps the click target the size of the content, not the cell.
const wrapInLightspeedLink = (
  block: React.ReactNode,
  link: string | null | undefined,
  popupLabel: string,
  options: { linkColor?: string; hoverScopeClass?: string } = {},
): React.ReactNode => {
  if (!link) return block
  const hoverSx = options.hoverScopeClass
    ? { [`&:hover .${options.hoverScopeClass}`]: { textDecoration: 'underline' } }
    : { '&:hover': { textDecoration: 'underline' } }
  return React.createElement(
    Link,
    {
      href: link,
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) =>
        handleLightspeedLinkClick(e, link, popupLabel),
      sx: {
        display: 'inline-block',
        textDecoration: 'none',
        cursor: 'pointer',
        ...(options.linkColor ? { color: options.linkColor } : null),
        ...hoverSx,
      },
    },
    block,
  )
}

export const renderProductName = (
  value: any,
  row: Record<string, any>,
  _ctx: RenderContext,
  options: ProductNameOptions = {},
): React.ReactNode => {
  const text = value ?? '-'
  const overlaySx: Record<string, any> = {
    ...(options.linkColor ? { color: options.linkColor } : null),
    ...(options.linkFontSize ? { fontSize: options.linkFontSize } : null),
    ...(options.nameMismatch ? MISMATCH_PILL_SX : null),
  }
  if (!row.lightspeed_product_link) {
    if (Object.keys(overlaySx).length === 0) return text
    return React.createElement(
      Box,
      { component: 'span', sx: overlaySx },
      text,
    )
  }
  return React.createElement(
    Link,
    {
      href: row.lightspeed_product_link,
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) =>
        handleLightspeedLinkClick(e, row.lightspeed_product_link, `Product ${text}`),
      sx: {
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
        fontWeight: 500,
        ...overlaySx,
      },
    },
    text,
  )
}

// Icons with visible right whitespace (pie-chart, widgets) need a -2px nudge.
const RIGHT_WHITESPACE_TYPES = new Set<ProductType>([
  'composite_dosage',
  'composite_kit',
])

const renderProductTypeBadge = (
  productType: ProductType,
): React.ReactNode => {
  const meta = PRODUCT_TYPE_META[productType]
  if (!meta) return null
  const Icon = meta.IconComponent
  const color = meta.colorHex ?? 'text.secondary'
  const trimRight = RIGHT_WHITESPACE_TYPES.has(productType)
  return withTooltip(
    { title: meta.long, arrow: true },
    React.createElement(
      Box,
      {
        component: 'span',
        sx: {
          display: 'inline-flex',
          alignItems: 'center',
          color,
          flexShrink: 0,
          ...(trimRight ? { marginRight: '-2px' } : null),
        },
      },
      React.createElement(Icon, { sx: { fontSize: 16 } }),
    ),
  )
}

const renderCategoryFolderBadge = (): React.ReactNode =>
  React.createElement(
    Box,
    {
      component: 'span',
      sx: {
        display: 'inline-flex',
        alignItems: 'center',
        color: 'text.secondary',
        flexShrink: 0,
      },
    },
    React.createElement(FolderOutlined, { sx: { fontSize: 16 } }),
  )

export const renderProductNameWithMeta = (
  value: any,
  row: Record<string, any>,
  ctx: RenderContext,
  options: ProductNameWithMetaOptions = {},
): React.ReactNode => {
  // Platform variant collapses to the dense single-line layout.
  if (ctx.variant === 'platform') {
    return renderProductName(value, row, ctx, {
      linkColor: options.linkColor,
      linkFontSize: options.linkFontSize,
      nameMismatch: options.nameMismatch,
    })
  }
  const showCategory = options.showCategory !== false
  const showTypeBadge = options.showTypeBadge !== false
  const showInactiveChip = options.showInactiveChip !== false
  const productType = showTypeBadge
    ? resolveProductType(row.product_type, row)
    : null
  const skuText = row.sku ? String(row.sku) : null
  const categoryText = showCategory
    ? formatMainCategory(row.category_root_name)
    : null
  const inactive = showInactiveChip && !isActiveValue(row.active)

  const nameText = value ?? '-'
  const nameSx: Record<string, any> = {
    fontWeight: 500,
    ...(options.linkFontSize ? { fontSize: options.linkFontSize } : null),
    ...(options.nameMismatch ? MISMATCH_PILL_SX : null),
  }
  const nameNode = React.createElement(
    Box,
    {
      component: 'span',
      className: PRODUCT_NAME_HOVER_CLASS,
      sx: nameSx,
    },
    nameText,
  )

  // Tighten icon-to-name gap when the mismatch pill adds its own padding.
  const titleSpacing = options.nameMismatch && productType ? 0.5 : 0.75
  const titleLine = React.createElement(
    Stack,
    { direction: 'row', alignItems: 'center', spacing: titleSpacing },
    productType ? renderProductTypeBadge(productType) : null,
    nameNode,
  )

  if (!skuText && !categoryText && !inactive) {
    return wrapInLightspeedLink(
      titleLine,
      row.lightspeed_product_link,
      `Product ${nameText}`,
      { linkColor: options.linkColor, hoverScopeClass: PRODUCT_NAME_HOVER_CLASS },
    )
  }

  const subtitleChildren: React.ReactNode[] = []
  const subtitleSx = {
    fontSize: '0.72rem',
    color: 'text.secondary',
    lineHeight: 1.2,
  } as const

  if (skuText) {
    const skuDisplay = truncateSkuMiddle(skuText)
    const isTruncated = skuDisplay !== skuText
    const skuOverlaySx: Record<string, any> = {
      ...(options.skuMismatch ? MISMATCH_PILL_SX : null),
    }
    const skuSx = { ...subtitleSx, fontFamily: 'monospace' } as const
    const skuInner = React.createElement(
      Box,
      { key: 'sku', component: 'span', sx: { ...skuSx, ...skuOverlaySx } },
      skuDisplay,
    )
    subtitleChildren.push(
      isTruncated
        ? withTooltip(
            { key: 'sku-tooltip', title: skuText, placement: 'top', arrow: true },
            React.createElement(
              Box,
              { component: 'span', sx: { display: 'inline-flex' } },
              skuInner,
            ),
          )
        : skuInner,
    )
  }

  if (skuText && categoryText) {
    subtitleChildren.push(
      React.createElement(
        Box,
        {
          key: 'sep',
          component: 'span',
          sx: { ...subtitleSx, color: 'text.disabled' },
        },
        '·',
      ),
    )
  }

  if (categoryText) {
    subtitleChildren.push(
      React.createElement(
        Typography,
        {
          key: 'category',
          variant: 'caption',
          sx: subtitleSx,
        },
        categoryText,
      ),
    )
  }
  if (inactive) {
    subtitleChildren.push(
      React.createElement(Chip, {
        key: 'inactive',
        label: 'Inactive',
        size: 'small',
        variant: 'outlined',
        sx: {
          height: 18,
          fontSize: '0.65rem',
          fontWeight: 500,
          color: '#616161',
          borderColor: '#bdbdbd',
          backgroundColor: '#f5f5f5',
          '& .MuiChip-label': { px: 0.75 },
        },
      }),
    )
  }

  const identityBlock = React.createElement(
    Stack,
    { direction: 'column', spacing: 0 },
    titleLine,
    React.createElement(
      Stack,
      {
        direction: 'row',
        alignItems: 'center',
        spacing: 0.75,
        // ~3.2px gap so the subtitle reads as a separate line without feeling disjoint.
        sx: { mt: 0.4 },
      },
      ...subtitleChildren,
    ),
  )

  return wrapInLightspeedLink(
    identityBlock,
    row.lightspeed_product_link,
    `Product ${nameText}`,
    { linkColor: options.linkColor, hoverScopeClass: PRODUCT_NAME_HOVER_CLASS },
  )
}

export const renderCategoryName = (
  value: any,
  row: Record<string, any>,
  _ctx: RenderContext,
  options: ProductNameOptions = {},
): React.ReactNode => {
  const text = formatMainCategory(value)
  const overlaySx: Record<string, any> = {
    ...(options.linkColor ? { color: options.linkColor } : null),
    ...(options.linkFontSize ? { fontSize: options.linkFontSize } : null),
    ...(options.nameMismatch ? MISMATCH_PILL_SX : null),
  }
  const link = row.lightspeed_category_link
  if (!link) {
    if (Object.keys(overlaySx).length === 0) return text
    return React.createElement(
      Box,
      { component: 'span', sx: overlaySx },
      text,
    )
  }
  return React.createElement(
    Link,
    {
      href: link,
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) =>
        handleLightspeedLinkClick(e, link, `Category ${text}`),
      sx: {
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
        fontWeight: 500,
        ...overlaySx,
      },
    },
    text,
  )
}

export const renderCategoryNameWithMeta = (
  value: any,
  row: Record<string, any>,
  _ctx: RenderContext,
  options: ProductNameOptions = {},
): React.ReactNode => {
  const text = formatMainCategory(value)
  const nameSx: Record<string, any> = {
    fontWeight: 500,
    ...(options.linkFontSize ? { fontSize: options.linkFontSize } : null),
    ...(options.nameMismatch ? MISMATCH_PILL_SX : null),
  }
  // Tighten icon-to-name gap when the mismatch pill adds its own padding.
  const titleSpacing = options.nameMismatch ? 0.5 : 0.75
  const identityBlock = React.createElement(
    Stack,
    { direction: 'row', alignItems: 'center', spacing: titleSpacing },
    renderCategoryFolderBadge(),
    React.createElement(
      Box,
      { component: 'span', className: PRODUCT_NAME_HOVER_CLASS, sx: nameSx },
      text,
    ),
  )
  return wrapInLightspeedLink(
    identityBlock,
    row.lightspeed_category_link,
    `Category ${text}`,
    { linkColor: options.linkColor, hoverScopeClass: PRODUCT_NAME_HOVER_CLASS },
  )
}

export const isActiveValue = (value: any): boolean =>
  typeof value === 'boolean' ? value : value === 'active' || value === 'Active'

export const renderProductStatus = (value: any): React.ReactNode => {
  const isActive = isActiveValue(value)
  return React.createElement(Chip, {
    size: 'small',
    variant: 'outlined',
    label: isActive ? 'Active' : 'Inactive',
    color: isActive ? 'success' : 'default',
    sx: {
      minWidth: 72,
      fontWeight: 500,
      ...(isActive ? {} : { backgroundColor: '#e0e0e0', color: '#616161' }),
    },
  })
}

export const productStatusSortValue = (row: Record<string, any>): number =>
  isActiveValue(row.active) ? 1 : 0

export const resolveProductType = (
  value: any,
  row: Record<string, any>,
): ProductType | null => {
  if (value && typeof value === 'string' && value in PRODUCT_TYPE_SORT_ORDER) {
    return value as ProductType
  }
  if (
    row.is_composite !== undefined ||
    row.has_variants !== undefined ||
    row.variant_parent_id !== undefined
  ) {
    return classifyProductType({
      is_composite: row.is_composite,
      has_variants: row.has_variants,
      variant_parent_id: row.variant_parent_id,
      composite_bom: row.composite_bom,
    })
  }
  return null
}

export const renderProductType = (
  value: any,
  row: Record<string, any>,
  _ctx: RenderContext,
): React.ReactNode => {
  const productType = resolveProductType(value, row)
  return React.createElement(ProductTypeChip, { productType, dense: true })
}

export const productTypeSortValue = (row: Record<string, any>): number => {
  const productType = resolveProductType(row.product_type, row)
  return productType ? PRODUCT_TYPE_SORT_ORDER[productType] : 99
}

export type ProductRowError = {
  code: string
  label: string
  tooltip?: string
  primary?: boolean
  // True when the underlying franchise_exception_tags row carries a
  // blocked_at annotation. Drives the red-when-any-blocked chip
  // colour in renderErrorCount + the "(blocked)" suffix in the
  // hover tooltip.
  blocked?: boolean
  // EVO/PSC reason captured at block time. Only present on the
  // primary blocked entry to avoid repeating the same string on
  // every chip in the tooltip.
  block_reason?: string
  // Reference links sourced from exception_type_links, filtered by FP
  // market. Present on non-product domain rows (sales/clients/systems)
  // that flow through backend/shared/exception_queries/row_errors.py.
  // Product rows currently omit it - the Popover renders no link chips
  // when this array is empty/absent.
  links?: import('../../../components/shared/referenceLinks/types').ReferenceLink[]
  // Closed-side only. 'resolved' or 'ignored'. Drives the closed-view
  // chip state (teal CheckCircle vs grey VisibilityOff). Present on
  // recently_closed_errors[] entries; absent on open errors[].
  kind?: 'resolved' | 'ignored'
  // Weight-0 roll-up (the sales reconciliation umbrella). Stamped by the
  // backend tag-table path; lets the shared ``actionableEntries`` helper
  // collapse it out of per-object counts (chip count + column sort) so
  // they match the drawer. Absent on product rows (never an umbrella).
  is_umbrella?: boolean
}

// Single count chip + hover preview + click Popover with the full
// per-issue list and Fix Guide links. The component is the canonical
// per-row state surface (the per-row "status pill" column is
// intentionally absent). State map and accessibility details live in
// IssuesChip.tsx.
export const renderErrorCount = (
  value: any,
  row: Record<string, any>,
  ctx: RenderContext,
): React.ReactNode => {
  const errors = (Array.isArray(value) ? value : row.errors) as
    | ProductRowError[]
    | undefined
  const recentlyClosed = row.recently_closed_errors as
    | ProductRowError[]
    | undefined
  const chip = React.createElement(IssuesChip, {
    errors,
    recentlyClosed,
    noteCount: ctx.noteCount,
    siblingNoteCount: ctx.siblingNoteCount,
    onOpenDrawer: ctx.openDrawer,
  })
  // The Issues column carries a single indicator: the notes dot (rendered by
  // IssuesChip when the row has notes). The fixed/awaiting-sync state lives in
  // the drawer, not as a second column dot - a row's "already fixed" status is
  // reviewed there, and a second near-teal dot here only competed with notes.
  return chip
}

export type OutletCostEntry = {
  outlet_id: string
  outlet_name: string
  average_cost: number
  inventory_level?: number
  stock_quantity?: number
  margin_percentage?: number | null
}

// Per-outlet stock/avg-cost cells render via the shared `OutletMetricCell`
// component (./OutletMetricCell.tsx), composed by `buildOutletCostColumns`.
// The previous `renderOutletAvgCost` / `renderOutletStockQty` pair was
// absorbed into it so formats, anomaly glyphs, and colour rules live in
// exactly one place.
